import { useState, useCallback, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useQueryClient } from '@tanstack/react-query'
import {
    RiArrowLeftLine,
    RiArrowRightLine,
    RiShieldCheckLine,
    RiRefreshLine,
    RiShoppingBag3Line,
    RiMapPinLine,
    RiAddLine,
    RiCheckLine,
    RiPencilLine,
} from 'react-icons/ri'
import {
    useCheckoutPay,
    useCheckoutSessionStatus,
} from '@/api/usePayments'
import type { MercadoPagoThreeDs } from '@/api/usePayments'
import { useAddresses } from '@/api/useAddresses'
import { useShippingConfig } from '@/api/useShippingConfig'
import { MercadoPagoForm, MercadoPagoStatusBrick } from '@/components/payments'
import { AddressFormModal } from '@/components/orders/AddressFormModal'
import { getDeviceId } from '@/lib/mercadopago'
import { useCartStore, selectItemsTotal, selectTotal } from '@/stores/useCartStore'
import { useAuthStore } from '@/stores/useAuthStore'
import { useModalStore } from '@/stores/useModalStore'
import { Spinner, Button } from '@/components/ui'
import { ROUTES } from '@/constants/routes'
import styles from './Checkout.module.css'

// Two-step wizard. Step 1 collects the shipping address (+ notes); step 2 shows
// the Mercado Pago Brick. The payment Brick is rendered ONLY on the 'payment'
// step, so it cannot be reached without first confirming a shipping address.
// This is internal state (single `/checkout` route) — there is no deep-link to
// the payment step without an address.
type WizardStep = 'address' | 'payment'

// Sub-views for the in-page payment flow (active on the 'payment' wizard step).
// The order does NOT exist until the payment confirms, so we never navigate to
// an order until we actually have one (the pay response spreads the
// OrderDetail, or the session poll resolves).
type CheckoutStep = 'ready' | 'verifying' | 'challenge' | 'error'

// Public key for the Mercado Pago Brick — safe to ship in the bundle.
const MERCADOPAGO_PUBLIC_KEY = (
    import.meta.env.VITE_MERCADOPAGO_PUBLIC_KEY as string | undefined
) || ''

// Generic fallback shown when the backend gave us no safe message (network
// error, etc.). Includes a support contact per the payment-hardening spec.
const GENERIC_PAYMENT_ERROR =
    'No pudimos procesar tu pago. Intenta de nuevo o escríbenos a info@qolca.org.'

/** Pull the backend's customer-safe `detail` message out of an Axios error. */
function extractErrorMessage(err: unknown): string {
    if (err && typeof err === 'object' && 'response' in err) {
        const resp = (err as { response?: { data?: { detail?: unknown } } }).response
        const msg = resp?.data?.detail
        if (typeof msg === 'string' && msg) return msg
    }
    return GENERIC_PAYMENT_ERROR
}

/**
 * `/checkout` — multi-step checkout under the order-on-payment model.
 *
 * The cart is the durable pre-payment state (persisted client-side and
 * resumable). The flow is split into two views:
 *
 *  1. "Dirección de envío" — the buyer selects a saved address (referenced by
 *     `shippingAddressId` on the cart store) and may leave order notes. The
 *     Mercado Pago Brick is NOT mounted here. "Continuar al pago" is disabled
 *     until a shipping address is selected.
 *  2. "Pago" — a read-only recap of the chosen address + order summary + the
 *     Mercado Pago Card Brick. "Editar" returns to step 1 (which unmounts the
 *     Brick). The Brick mounts ONLY on this step.
 *
 * On Brick submit it tokenises the card via the Brick, then calls
 * `POST /api/checkout/pay` — which creates the order ONLY when the payment
 * confirms:
 *
 *  - approved (`paid === true`) → success toast + navigate to the new order.
 *  - `pending_challenge`        → mount the Status Screen Brick (3DS), then poll
 *    `GET /api/checkout/session/<uuid>/status` until `paid` (→ orders) or
 *    `failed`/`expired` (→ masked error, cart intact).
 *  - `pending` / `in_process`   → "te confirmaremos por correo" path.
 *  - rejected (402)             → masked `{detail}` toast, cart stays intact.
 *
 * If the buyer abandons or the card is declined, no order is created and the
 * cart is untouched — they can come back and pay again from the cart.
 */
export function Checkout() {
    const navigate = useNavigate()
    const qc = useQueryClient()

    const items = useCartStore((s) => s.items)
    const shippingAddressId = useCartStore((s) => s.shippingAddressId)
    const shippingCost = useCartStore((s) => s.shippingCost)
    const notes = useCartStore((s) => s.notes)
    const setShippingAddress = useCartStore((s) => s.setShippingAddress)
    const setNotes = useCartStore((s) => s.setNotes)
    const clearCart = useCartStore((s) => s.clearCart)
    const itemsTotal = useCartStore(selectItemsTotal)
    const total = useCartStore(selectTotal)

    // The auth store exposes the user only via a getter; re-derive once per
    // render. The email pre-fills the MP Brick.
    const getUser = useAuthStore((s) => s.getUser)
    const payerEmail = getUser()?.email || ''

    const { data: addresses, isLoading: loadingAddresses } = useAddresses()
    const { data: shippingConfigs } = useShippingConfig()
    const openModal = useModalStore((s) => s.openModal)

    const pay = useCheckoutPay()

    // Wizard view: 'address' (step 1) → 'payment' (step 2). Always starts on
    // step 1 so the payment Brick can never mount without a chosen address.
    const [wizardStep, setWizardStep] = useState<WizardStep>('address')

    const [step, setStep] = useState<CheckoutStep>('ready')
    const [errorMessage, setErrorMessage] = useState<string | null>(null)

    // Set when the backend returns `pending_challenge`. While present
    // (step === 'challenge') the Status Screen Brick is mounted.
    const [challenge, setChallenge] = useState<{
        sessionUuid: string
        paymentId: string
        threeDs: MercadoPagoThreeDs
    } | null>(null)
    // Once the challenge is presented + resolved, start polling the session.
    const [pollingUuid, setPollingUuid] = useState<string | null>(null)

    // ── Auto-select default address on load (matches the cart behaviour) ──────
    useEffect(() => {
        if (addresses && addresses.length > 0 && !shippingAddressId) {
            const defaultAddr = addresses.find((a) => a.is_default) || addresses[0]
            const config = shippingConfigs?.find((s) => s.department === defaultAddr.department)
            const cost = config ? parseFloat(config.surcharge) : 0
            setShippingAddress(defaultAddr.id, cost)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addresses, shippingConfigs])

    const handleAddressSelect = (addressId: number) => {
        const addr = addresses?.find((a) => a.id === addressId)
        if (!addr) return
        const config = shippingConfigs?.find((s) => s.department === addr.department)
        const cost = config ? parseFloat(config.surcharge) : 0
        setShippingAddress(addressId, cost)
    }

    // ── Session-status poll (after a 3DS challenge) ──────────────────────────
    const sessionStatus = useCheckoutSessionStatus(pollingUuid, !!pollingUuid)
    const sessionData = sessionStatus.data

    useEffect(() => {
        if (!pollingUuid || !sessionData) return

        if (sessionData.status === 'paid') {
            setPollingUuid(null)
            setChallenge(null)
            clearCart()
            void qc.invalidateQueries({ queryKey: ['orders'] })
            toast.success('Pago confirmado. Tu pedido fue registrado.')
            // The session status only returns order_number (no uuid), and the
            // order detail route is keyed by uuid — send the buyer to their
            // orders list, where the new paid order is now visible.
            navigate(ROUTES.orders)
        } else if (sessionData.status === 'failed' || sessionData.status === 'expired') {
            setPollingUuid(null)
            setChallenge(null)
            setErrorMessage(GENERIC_PAYMENT_ERROR)
            setStep('error')
            toast.error(GENERIC_PAYMENT_ERROR)
        }
    }, [pollingUuid, sessionData, qc, navigate, clearCart])

    /**
     * The Brick has tokenised the card and is calling us with the form data.
     * Hand it off to /api/checkout/pay and branch on the response. Returning a
     * rejected promise keeps the Brick in its error state so the buyer can see
     * the toast and retry; the cart is left intact on any failure.
     */
    const handlePaymentSubmit = useCallback(
        async (cardFormData: MercadoPagoCardFormData) => {
            if (!shippingAddressId) {
                toast.error('Selecciona una dirección de envío')
                throw new Error('missing_address')
            }

            setErrorMessage(null)
            setStep('verifying')

            try {
                const result = await pay.mutateAsync({
                    items: items.map((item) => ({
                        project: item.projectId,
                        front_page: item.frontPageId,
                        back_page: item.backPageId,
                        material: item.materialId,
                        size: item.sizeId,
                        quantity_millares: item.quantityMillares,
                    })),
                    shippingAddress: shippingAddressId,
                    notes: notes.trim() || undefined,
                    token: cardFormData.token,
                    paymentMethodId: cardFormData.payment_method_id,
                    issuerId: cardFormData.issuer_id,
                    installments: cardFormData.installments || 1,
                    deviceId: getDeviceId(),
                    payer: cardFormData.payer,
                })

                if ('paid' in result) {
                    // Approved: the order now exists (the backend spreads the
                    // full OrderDetail, whose own `status` is e.g. "pending"
                    // awaiting admin confirmation — so we key off `paid`, not a
                    // `status:'approved'`). Clear the cart and route to it.
                    clearCart()
                    void qc.invalidateQueries({ queryKey: ['orders'] })
                    toast.success('Pago realizado exitosamente')
                    if (result.uuid) {
                        navigate(ROUTES.orderDetail.replace(':uuid', result.uuid))
                    } else {
                        navigate(ROUTES.orders)
                    }
                    return
                }

                if (result.status === 'pending_challenge') {
                    // No order yet — mount the Status Screen Brick for the bank
                    // challenge, then poll the session until it resolves.
                    setChallenge({
                        sessionUuid: result.session_uuid,
                        paymentId: result.payment_id || '',
                        threeDs: result.three_ds,
                    })
                    setStep('challenge')
                    return
                }

                // pending / in_process / processing — the webhook will confirm;
                // the buyer will be notified by email. No order exists yet, so
                // we can't route to one. Leave the cart intact and acknowledge.
                setStep('ready')
                toast.success('Estamos revisando tu pago. Te confirmaremos por correo.')
                navigate(ROUTES.orders)
            } catch (err) {
                // Rejected (402) or network error — render ONLY the backend-safe
                // `detail`. The cart is intact; the buyer can retry.
                const message = extractErrorMessage(err)
                setErrorMessage(message)
                setStep('error')
                toast.error(message)
                // Rethrow keeps the Brick in its error state.
                throw err
            }
        },
        [items, shippingAddressId, notes, pay, clearCart, qc, navigate],
    )

    const handlePaymentError = useCallback((message: string) => {
        setErrorMessage(message)
        setStep('error')
        toast.error(message)
    }, [])

    /**
     * The Status Screen Brick presented + resolved the 3DS challenge. We do not
     * read MP's outcome client-side — the session status (driven by the
     * webhook) is the source of truth. Start polling it.
     */
    const handleChallengeResolved = useCallback(() => {
        if (challenge) setPollingUuid(challenge.sessionUuid)
    }, [challenge])

    const resetToReady = useCallback(() => {
        setStep('ready')
        setErrorMessage(null)
        setChallenge(null)
        setPollingUuid(null)
    }, [])

    /** Advance from the address step to the payment step. */
    const handleContinueToPayment = useCallback(() => {
        if (!shippingAddressId) {
            toast.error('Selecciona una dirección de envío')
            return
        }
        setWizardStep('payment')
    }, [shippingAddressId])

    /**
     * "Editar" — return to the address step. Unmounting the payment view tears
     * the Brick down and resets any in-flight payment sub-state so a fresh
     * Brick mounts when the buyer comes back.
     */
    const handleEditAddress = useCallback(() => {
        resetToReady()
        setWizardStep('address')
    }, [resetToReady])

    // ── Empty-cart guard ─────────────────────────────────────────────────────
    if (items.length === 0) {
        return (
            <div className={styles.container}>
                <Link to={ROUTES.cart} className={styles.backLink}>
                    <RiArrowLeftLine />
                    Volver al carrito
                </Link>
                <div className={styles.emptyState}>
                    <RiShoppingBag3Line className={styles.emptyIcon} />
                    <h2 className={styles.emptyTitle}>Tu carrito está vacío</h2>
                    <p className={styles.emptyText}>
                        Agrega un producto antes de pagar.
                    </p>
                    <Button variant="primary" onClick={() => navigate(ROUTES.projects)}>
                        Ir a mis proyectos
                    </Button>
                </div>
            </div>
        )
    }

    const formatSoles = (amount: string | number) => `S/ ${Number(amount).toFixed(2)}`

    const selectedAddr = addresses?.find((a) => a.id === shippingAddressId)
    const onAddressStep = wizardStep === 'address'

    return (
        <div className={styles.container}>
            <Link to={ROUTES.cart} className={styles.backLink}>
                <RiArrowLeftLine />
                Volver al carrito
            </Link>

            <h1 className={styles.pageTitle}>Finalizar compra</h1>

            {/* Stepper: 1. Envío → 2. Pago */}
            <nav className={styles.stepper} aria-label="Progreso de la compra">
                <div
                    className={`${styles.stepperItem} ${onAddressStep ? styles.stepperActive : styles.stepperDone}`}
                    aria-current={onAddressStep ? 'step' : undefined}
                >
                    <span className={styles.stepperBadge}>
                        {onAddressStep ? '1' : <RiCheckLine />}
                    </span>
                    <span className={styles.stepperLabel}>Envío</span>
                </div>
                <span className={styles.stepperSeparator} aria-hidden="true">
                    <RiArrowRightLine />
                </span>
                <div
                    className={`${styles.stepperItem} ${onAddressStep ? '' : styles.stepperActive}`}
                    aria-current={onAddressStep ? undefined : 'step'}
                >
                    <span className={styles.stepperBadge}>2</span>
                    <span className={styles.stepperLabel}>Pago</span>
                </div>
            </nav>

            <div className={styles.layout}>
                {/* Left column — switches between the address and payment views */}
                <div className={styles.paymentSection}>
                    {onAddressStep ? (
                        <>
                            {/* ── STEP 1: shipping address ───────────────────── */}
                            <div className={styles.card}>
                                <h2 className={styles.cardTitle}>
                                    <RiMapPinLine />
                                    Dirección de envío
                                </h2>

                                {loadingAddresses ? (
                                    <div className={styles.formLoading} aria-busy="true">
                                        <Spinner size="sm" />
                                        <p>Cargando direcciones...</p>
                                    </div>
                                ) : addresses && addresses.length > 0 ? (
                                    <div className={styles.addressGrid}>
                                        {addresses.map((addr) => (
                                            <button
                                                key={addr.id}
                                                type="button"
                                                className={`${styles.addressCard} ${shippingAddressId === addr.id ? styles.addressSelected : ''}`}
                                                onClick={() => handleAddressSelect(addr.id)}
                                                aria-pressed={shippingAddressId === addr.id}
                                            >
                                                <strong className={styles.addressLabel}>{addr.label}</strong>
                                                <span className={styles.addressLine}>{addr.address_line}</span>
                                                <span className={styles.addressMeta}>
                                                    {addr.district}, {addr.province}
                                                </span>
                                                <span className={styles.addressDept}>{addr.department_display}</span>
                                                {addr.is_default && (
                                                    <span className={styles.defaultBadge}>Predeterminada</span>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                ) : (
                                    <p className={styles.noAddresses}>No tienes direcciones guardadas.</p>
                                )}

                                <button
                                    type="button"
                                    className={styles.addAddressBtn}
                                    onClick={() => openModal(<AddressFormModal />)}
                                >
                                    <RiAddLine />
                                    Agregar nueva dirección
                                </button>

                                {selectedAddr && (
                                    <div className={styles.shippingInfo}>
                                        <RiMapPinLine className={styles.shippingIcon} />
                                        <span>
                                            Envío a <strong>{selectedAddr.department_display}</strong>
                                            {shippingCost > 0
                                                ? `: ${formatSoles(shippingCost)}`
                                                : ': Incluido'}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Notes */}
                            <div className={styles.card}>
                                <h2 className={styles.cardTitle}>
                                    Notas del pedido
                                    <span className={styles.optional}>(opcional)</span>
                                </h2>
                                <textarea
                                    className={styles.notesInput}
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Instrucciones especiales para la impresión o entrega..."
                                    rows={3}
                                />
                            </div>

                            <div className={styles.stepActions}>
                                <Button
                                    variant="primary"
                                    onClick={handleContinueToPayment}
                                    disabled={!shippingAddressId}
                                    className={styles.continueBtn}
                                >
                                    Continuar al pago
                                    <RiArrowRightLine />
                                </Button>
                            </div>
                        </>
                    ) : (
                        <>
                            {/* ── STEP 2: payment ────────────────────────────── */}
                            {/* Read-only recap of the chosen address (Editar → step 1). */}
                            <div className={styles.card}>
                                <h2 className={styles.cardTitle}>
                                    <RiMapPinLine />
                                    Envío a
                                </h2>
                                {selectedAddr ? (
                                    <div className={styles.recapBlock}>
                                        <div className={styles.recapAddress}>
                                            <strong className={styles.addressLabel}>{selectedAddr.label}</strong>
                                            <span className={styles.addressLine}>{selectedAddr.address_line}</span>
                                            <span className={styles.addressMeta}>
                                                {selectedAddr.district}, {selectedAddr.province}
                                            </span>
                                            <span className={styles.addressDept}>
                                                {selectedAddr.department_display}
                                                {shippingCost > 0
                                                    ? ` · Envío ${formatSoles(shippingCost)}`
                                                    : ' · Envío incluido'}
                                            </span>
                                            {notes.trim() && (
                                                <span className={styles.recapNotes}>
                                                    Notas: {notes.trim()}
                                                </span>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            className={styles.editLink}
                                            onClick={handleEditAddress}
                                        >
                                            <RiPencilLine />
                                            Editar
                                        </button>
                                    </div>
                                ) : (
                                    <p className={styles.noAddresses}>
                                        No hay una dirección seleccionada.{' '}
                                        <button
                                            type="button"
                                            className={styles.editLinkInline}
                                            onClick={handleEditAddress}
                                        >
                                            Volver al envío
                                        </button>
                                    </p>
                                )}
                            </div>

                            {/* Payment method (the Mercado Pago Brick + its
                                verifying / challenge / error sub-states). */}
                            <div className={styles.card}>
                                <h2 className={styles.cardTitle}>Método de pago</h2>

                                {/* Verifying: the /pay request is in flight (no
                                    challenge yet) or we are polling the session. */}
                                {step === 'verifying' && (
                                    <div
                                        className={styles.formLoading}
                                        aria-live="polite"
                                        aria-busy="true"
                                    >
                                        <Spinner size="md" />
                                        <p>Verificando tu pago...</p>
                                    </div>
                                )}

                                {/* 3DS challenge — the Status Screen Brick renders
                                    the bank challenge, then we poll the session. */}
                                {step === 'challenge' && challenge && (
                                    <>
                                        <MercadoPagoStatusBrick
                                            publicKey={MERCADOPAGO_PUBLIC_KEY}
                                            paymentId={challenge.paymentId}
                                            externalResourceUrl={challenge.threeDs.external_resource_url}
                                            creq={challenge.threeDs.creq}
                                            onChallengeResolved={handleChallengeResolved}
                                            onError={handlePaymentError}
                                        />
                                        {!!pollingUuid && (
                                            <div
                                                className={styles.formLoading}
                                                aria-live="polite"
                                                aria-busy="true"
                                            >
                                                <Spinner size="sm" />
                                                <p>Verificando tu pago...</p>
                                            </div>
                                        )}
                                    </>
                                )}

                                {/* Error — masked message + retry (cart intact). */}
                                {step === 'error' && (
                                    <div className={styles.errorState}>
                                        <p className={styles.errorText}>
                                            {errorMessage ?? GENERIC_PAYMENT_ERROR}
                                        </p>
                                        <Button variant="secondary" onClick={resetToReady}>
                                            <RiRefreshLine />
                                            Reintentar
                                        </Button>
                                    </div>
                                )}

                                {/* Ready — the MP Brick (its submit button is the
                                    single "Pagar" action). Mounts ONLY on step 2. */}
                                {step === 'ready' && (
                                    <MercadoPagoForm
                                        publicKey={MERCADOPAGO_PUBLIC_KEY}
                                        amount={total}
                                        payerEmail={payerEmail}
                                        onSubmitPayment={handlePaymentSubmit}
                                        onError={handlePaymentError}
                                    />
                                )}
                            </div>

                            <div className={styles.secureRow}>
                                <RiShieldCheckLine className={styles.secureIcon} />
                                <span>
                                    Pago seguro procesado por Mercado Pago. Tus datos están protegidos.
                                </span>
                            </div>

                            {/* Back to the address step. Disabled while a payment
                                is mid-flight to avoid tearing it down. */}
                            {(step === 'ready' || step === 'error') && (
                                <div className={styles.stepActions}>
                                    <Button
                                        variant="secondary"
                                        onClick={handleEditAddress}
                                        className={styles.backBtn}
                                    >
                                        <RiArrowLeftLine />
                                        Volver al envío
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Right: order summary */}
                <div className={styles.summarySection}>
                    <div className={styles.summaryCard}>
                        <h2 className={styles.summaryTitle}>
                            <RiShoppingBag3Line />
                            Resumen del pedido
                        </h2>

                        <div className={styles.summaryMeta}>
                            <span>
                                {items.length} {items.length === 1 ? 'producto' : 'productos'}
                            </span>
                        </div>

                        <div className={styles.itemsList}>
                            {items.map((item) => (
                                <div key={item.id} className={styles.summaryItem}>
                                    <div className={styles.summaryItemInfo}>
                                        <p className={styles.summaryItemName}>
                                            {item.projectName}
                                        </p>
                                        <p className={styles.summaryItemMeta}>
                                            {item.materialName} · {item.sizeName} ·{' '}
                                            {item.quantityMillares} millar
                                            {item.quantityMillares !== 1 ? 'es' : ''}
                                        </p>
                                    </div>
                                    <span className={styles.summaryItemPrice}>
                                        {formatSoles(item.subtotal)}
                                    </span>
                                </div>
                            ))}
                        </div>

                        <div className={styles.summaryDivider} />

                        <div className={styles.summaryTotals}>
                            <div className={styles.summaryRow}>
                                <span>Subtotal</span>
                                <span>{formatSoles(itemsTotal)}</span>
                            </div>
                            <div className={styles.summaryRow}>
                                <span>Envío</span>
                                <span>
                                    {shippingCost > 0 ? formatSoles(shippingCost) : 'Incluido'}
                                </span>
                            </div>
                            <div className={`${styles.summaryRow} ${styles.totalRow}`}>
                                <span>Total a pagar</span>
                                <span>{formatSoles(total)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
